import React, { Fragment, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, GridLegacy as Grid, Typography } from "@mui/material";
import { IProductCompletedCreate, ProductCompleted, ProductSaleItem, ProductTypes, SourceApp } from "../../../../logic/api";
import { useCreateProductCompleted } from "../../../../hooks/useProductsCompleted";
import { useDate } from "../../../../utility/useDate";
import { ProgressView } from "../../../../components/notifications/progress/ProgressView";
import EditableGroup, { EditableGroupType } from "../../../../components/editable/EditableGroup/EditableGroup";
import EditableField, { EditableFieldTypes } from "../../../../components/editable/EditableField/EditableField";
import { Trans, useTranslation } from "react-i18next";
import { RequestStatus } from "../../../../logic/api";

interface ProductCompletedDialogProps {
  open: boolean;
  productSaleItem: ProductSaleItem | null;
  bookingId: number;
  existingRecord?: ProductCompleted | null;
  onClose: () => void;
  onCompleted: () => void;
}

const ProductCompletedDialog: React.FC<ProductCompletedDialogProps> = ({ open, productSaleItem, bookingId, existingRecord, onClose, onCompleted }) => {
  const { t } = useTranslation();
  const { localizeDate, toFormattedDateTimeString } = useDate();
  const createMutation = useCreateProductCompleted(bookingId);

  const [productCompletedCreate, setProductCompletedCreate] = useState<IProductCompletedCreate>({
    bookingId,
    completedDate: localizeDate(new Date()).toISO() ?? "",
    remarks: "",
    productType: productSaleItem?.productType ?? ProductTypes.default,
    sourceApp: SourceApp.frontEnd,
  });

  const isViewMode = !!existingRecord;

  const completeProduct = () => {
    if (productSaleItem == null) return;
    createMutation.mutate(
      { ...productCompletedCreate, bookingId, productType: productSaleItem.productType, productSaleItemId: productSaleItem.id },
      {
        onSuccess: () => {
          onClose();
          onCompleted();
        },
      }
    );
  };

  if (productSaleItem == null) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isViewMode ? (
          <Trans i18nKey="product.productCompleted">
            {{ productName: productSaleItem.name } as any} completed
          </Trans>
        ) : (
          <Trans i18nKey="product.setProductCompleted">
            Set {{ productName: productSaleItem.name } as any} completed
          </Trans>
        )}
      </DialogTitle>
      <DialogContent style={{ minHeight: isViewMode ? undefined : 250 }}>
        {isViewMode ? (
          <div>
            <Typography variant="body2" sx={{ mt: 1, mb: 0.5, color: "text.secondary" }}>
              {t("product.labels.completedDate")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {toFormattedDateTimeString(existingRecord.completedDate)}
            </Typography>

            <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
              {t("product.labels.completedBy")}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {existingRecord.completedBy
                ? `${existingRecord.completedBy.firstName} ${existingRecord.completedBy.lastName}`
                : "-"}
            </Typography>

            {existingRecord.remarks && (
              <>
                <Typography variant="body2" sx={{ mb: 0.5, color: "text.secondary" }}>
                  {t("product.labels.remarks")}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {existingRecord.remarks}
                </Typography>
              </>
            )}
          </div>
        ) : (
          <Fragment>
            {createMutation.status === "pending" && (
              <ProgressView status={RequestStatus.loading} />
            )}

            <div>
              <EditableGroup<IProductCompletedCreate>
                id="completedItemDetails"
                testId="product-completed-dialog-group"
                editType={EditableGroupType.alwaysEditing}
                data={productCompletedCreate}
                onDataChanged={async (data) => {
                  setProductCompletedCreate(data);
                  return true;
                }}
              >
                <Grid item container>
                  <EditableField<IProductCompletedCreate, string>
                    inputType={EditableFieldTypes.date}
                    label={t("product.labels.completedDate")}
                    onProvide={(data) => data.completedDate}
                    onChange={(data, value) => {
                      data.completedDate = value;
                    }}
                  />
                  <EditableField<IProductCompletedCreate, string>
                    inputType={EditableFieldTypes.time}
                    label={t("product.labels.completedTime")}
                    onProvide={(data) => data.completedDate}
                    onChange={(data, value) => {
                      data.completedDate = value;
                    }}
                  />
                </Grid>
              </EditableGroup>
            </div>
          </Fragment>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("general.close")}</Button>
        {!isViewMode && (
          <Button onClick={completeProduct}>{t("general.update")}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ProductCompletedDialog;
